import { useApolloClient } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { useEffect, useRef } from 'react';
import type { ValidationErrorHandler } from './getErrorLink';
import { gql } from '../graphql/generated';
import { UIActions } from '../ui/uiSlice';
import useAppSelector, { useAppStore } from './redux/useAppSelector';

const CURRENT_VERSION_QUERY = gql(/** @complexity 1 */ `
  query currentVersion {
    currentVersion {
      version
    }
  }
`);

interface CheckVersionOptions {
  client: ApolloClient<object>;
  store: ReturnType<typeof useAppStore>;
}

export function isServerVersionCompatible(serverVersion: string) {
  const [clientMajor, clientMinor] = window.__slab.VERSION.split('.');
  const [serverMajor, serverMinor] = serverVersion.split('.');

  return serverMinor === clientMinor && serverMajor === clientMajor;
}

const checkVersion = async ({
  client,
  store,
}: CheckVersionOptions): ReturnType<ValidationErrorHandler> => {
  if (store.getState().ui.isVersionMismatchModalOpen) {
    return { isVersionMismatchModalOpen: true };
  }

  const queryResult = await client.query({
    query: CURRENT_VERSION_QUERY,
    fetchPolicy: 'network-only',
  });

  if (!isServerVersionCompatible(queryResult.data.currentVersion.version)) {
    store.dispatch(UIActions.openVersionMismatchModal());
    return { isVersionMismatchModalOpen: true };
  }

  return { isVersionMismatchModalOpen: false };
};

export function useVersionCheck() {
  const client = useApolloClient();
  const store = useAppStore();

  const socket = useAppSelector((state) => state.session.socket);
  const prevSocketRef = useRef<typeof socket>();

  useEffect(() => {
    if (socket) {
      socket.onOpen(() => {
        checkVersion({ client, store });
      });
    }

    if (prevSocketRef.current == null && socket != null) {
      checkVersion({ client, store });
    }

    prevSocketRef.current = socket;
  }, [socket, client, store]);
}

export default checkVersion;
